import React, { useContext, useEffect, useRef, useState } from "react";
import themeContext from "../../../../context/theme/ThemeContext";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import { resizeViewport } from "../../helpers/CornerstoneViewportService";
import LayoutSelectorIcon from "../../../../components/SVGComponents/LayoutSelectorIcon";
import { toolStyle } from "../../styles/styles";

// Mobile layout picker. The desktop grid selector relies on hover-to-preview,
// which doesn't work on touch, so mobile uses a tappable LIST of presets (like
// MedDream). Each entry directly applies changeLayout(rows, cols). Presets are
// capped at 4x4 (the viewer's max grid — see VIEWPORT_ROW/VIEWPORT_COLUMN).
const LAYOUT_PRESETS = [
  { rows: 1, cols: 1 },
  { rows: 1, cols: 2 },
  { rows: 2, cols: 1 },
  { rows: 2, cols: 2 },
  { rows: 2, cols: 3 },
  { rows: 2, cols: 4 },
  { rows: 3, cols: 1 },
  { rows: 3, cols: 2 },
  { rows: 3, cols: 3 },
  { rows: 4, cols: 2 },
  { rows: 4, cols: 4 },
];

const LayoutPreviewIcon = ({ rows, cols, active }) => (
  <span
    style={{
      display: "grid",
      gap: "1px",
      width: "18px",
      height: "18px",
      gridTemplateRows: `repeat(${rows}, 1fr)`,
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      flexShrink: 0,
    }}
  >
    {Array.from({ length: rows * cols }).map((_, i) => (
      <span
        key={i}
        style={{
          border: `1px solid ${active ? "#0085FF" : "#C1C1C1"}`,
          borderRadius: "1px",
        }}
      />
    ))}
  </span>
);

const MobileLayoutMenu = () => {
  const { theme } = useContext(themeContext);
  const { changeLayout, viewportRow, viewportColumn } =
    useContext(ViewportContext);
  const [showMenu, setShowMenu] = useState(false);
  // Bottom toolbar -> dropdown opens UPWARD (anchor bottom edge above the button).
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const toggleMenu = () => {
    setShowMenu((prev) => {
      const next = !prev;
      if (next && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPos({
          bottom: window.innerHeight - rect.top + 6,
          left: rect.left,
        });
      }
      return next;
    });
  };

  const applyLayout = (rows, cols) => {
    changeLayout(rows, cols);
    setShowMenu(false);
    setTimeout(async () => {
      await resizeViewport();
    }, 300);
  };

  return (
    <div
      ref={menuRef}
      style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div ref={buttonRef} onClick={toggleMenu} style={{ ...toolStyle }}>
        <div
          style={{
            height: "65%",
            width: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="highlight-tool-mobile">
            <LayoutSelectorIcon
              color={showMenu ? theme.palette.activeTool : theme.palette.inactiveTool}
            />
          </div>
        </div>
        <span
          style={{
            fontSize: "9px",
            lineHeight: "100%",
            fontWeight: "400",
            color: "var(--mobile-color-txt)",
            display: "block",
            width: "60px",
            textAlign: "center",
          }}
        >
          Layout
        </span>
      </div>

      {showMenu && (
        <div
          className="viewer-tool-more-dropdown viewer-tool-more-dropdown-height"
          style={{
            position: "fixed",
            bottom: menuPos.bottom,
            left: menuPos.left,
            zIndex: 9999,
            minWidth: "160px",
            maxHeight: "60vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            borderRadius: "5px",
            paddingTop: "6px",
            paddingBottom: "6px",
            boxShadow:
              "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
          }}
        >
          {LAYOUT_PRESETS.map(({ rows, cols }) => {
            const active = viewportRow === rows && viewportColumn === cols;
            return (
              <div
                key={`${rows}x${cols}`}
                className="stack-scroll-dropdown-elements"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 14px",
                  cursor: "pointer",
                  background: active ? "rgba(255, 184, 1, 0.12)" : "transparent",
                }}
                onClick={() => applyLayout(rows, cols)}
              >
                <LayoutPreviewIcon rows={rows} cols={cols} active={active} />
                <span
                  style={{
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    color: "var(--mobile-color-txt)",
                  }}
                >
                  {rows}x{cols} Layout
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileLayoutMenu;
